import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { Callout, FormGroup, InputGroup } from "@blueprintjs/core";

import { AppToaster, GreenButton, Link, SsoSetupForm } from "@components";
import { OrgContext, UserContext } from "@providers";
import { SsoProviderSettings } from "@types";
import { confirmPrompt, withToaster } from "@utils";

import { slugifyOrgName } from "@pages/dashboard/utils";
import {
  deleteAuthProvider,
  enableSsoProvider,
  fetchSsoProvider,
} from "@pages/settings/api";

const WarnText = styled.p`
  font-size: var(--global-font-size-xs);
  color: var(--global-red);
`;

const MarginCallout = styled(Callout)`
  margin-bottom: 12px;
`;

type Props = {
  deploymentId: string;
  providerId: string;
  hasTeamTier: boolean;
};

export const SsoPanel: React.FC<Props> = ({
  deploymentId,
  providerId,
  hasTeamTier,
}) => {
  const [emailDomain, setEmailDomain] = useState<string>("");
  const { org } = useContext(OrgContext);
  const { user } = useContext(UserContext);
  const [currentSettings, setCurrentSettings] =
    useState<SsoProviderSettings | null>(null);
  const source_type = org?.source_type;
  const capitalized_source_types = { github: "GitHub", gitlab: "GitLab" };

  useEffect(() => {
    fetchSsoProvider(deploymentId, "openid").then((resp) => {
      if (resp.settings !== undefined) {
        setCurrentSettings(resp.settings);
      } else {
        fetchSsoProvider(deploymentId, "saml2").then((resp) => {
          if (resp.settings !== undefined) {
            setCurrentSettings(resp.settings);
          } else {
            setCurrentSettings(null);
          }
        });
      }
    });
  }, [deploymentId]);

  const saveSSO = (ssoSettings: SsoProviderSettings) => {
    confirmPrompt(
      `Are you sure you want to update the SSO settings for @${ssoSettings.email_domain}`
    );
    withToaster(
      () =>
        enableSsoProvider(ssoSettings).then(() =>
          AppToaster.show({
            message: "Auth provider updated. Please sign out and in again.",
            intent: "success",
          })
        ),
      (err) => `Failed save SSO settings for ${providerId}: ${err.body?.error}`
    )();
  };

  return (
    <div style={{ maxWidth: "600px", marginTop: "12px" }}>
      {currentSettings && (
        <MarginCallout title="SSO Configured" intent="success">
          This deployment has been configured for SSO as follows. You can update
          your settings using the form below.
          <ul>
            <li key="type">{`SSO Type: ${currentSettings.provider_type}`}</li>
            <li key="domain">{`Email Domain: ${currentSettings.email_domain}`}</li>
            <li key="base_url">{`Base URL: ${currentSettings.base_url}`}</li>
          </ul>
        </MarginCallout>
      )}
      {!hasTeamTier && (
        <WarnText>
          This feature is only available to Team Tier users.{" "}
          <u>
            <Link
              to={`/orgs/${slugifyOrgName(org?.name ?? "-")}/settings/payment`}
            >
              Upgrade now
            </Link>
          </u>{" "}
          to get access.
        </WarnText>
      )}
      {hasTeamTier && source_type && user?.auth_provider_type !== source_type && (
        <div style={{ marginTop: "20px", marginBottom: "20px" }}>
          <div style={{ marginBottom: "12px" }}>
            Would you like to make SSO your only method of authentication?
          </div>
          <GreenButton
            onClick={() => {
              deleteAuthProvider(deploymentId, source_type)
                .then(() => {
                  AppToaster.show({
                    message: `Deleted ${source_type} auth`,
                    intent: "success",
                  });
                })
                .catch(() => {
                  AppToaster.show({
                    message: `Failed to delete ${source_type} auth`,
                    intent: "danger",
                  });
                });
            }}
          >
            Yes, disable auth with {capitalized_source_types[source_type]}
          </GreenButton>
        </div>
      )}
      <FormGroup
        label="Email Domain"
        labelFor="email-domain"
        helperText="Any email address with this domain will be directed to the given auth provider"
        disabled={!hasTeamTier}
      >
        <InputGroup
          placeholder="required"
          required
          id="email-domain"
          value={emailDomain}
          onChange={(e: React.FormEvent<HTMLElement>) => {
            setEmailDomain((e.target as HTMLInputElement).value);
          }}
          disabled={!hasTeamTier}
        />
      </FormGroup>

      <SsoSetupForm
        handleSaveSSO={saveSSO}
        providerId={providerId}
        deploymentId={deploymentId}
        emailDomain={emailDomain}
        disabled={!hasTeamTier}
      />
    </div>
  );
};
